import React, { Fragment } from 'react';
import { Link, NavLink, Route, Routes } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import cn from 'classnames';
import { CaretDown, CaretUp, X } from '@phosphor-icons/react';
import logo from 'assets/images/emessage-logo.png';
import { isUndefined, startCase } from 'lodash';
import PropTypes from 'prop-types';
import { useRoutePermission } from 'modules/auth/hooks';
import { usePendingOnboardingCount } from 'modules/for-verification/hooks';

const FOCUS =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-inset';

/*
 * Selection is one treatment product-wide: brand-50 fill plus an inset 3px
 * brand-600 bar. An expanded parent gets brand-700 text and a Bold icon but
 * NO fill, so parent and leaf never compete.
 */
const SELECTED = 'bg-brand-50 shadow-[inset_3px_0_0_rgb(var(--brand-600))]';

function NavItem({ label, icon: Icon, to, withNested }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'h-10 flex items-center justify-between px-3 transition-colors',
          FOCUS,
          // eslint-disable-next-line no-nested-ternary
          isActive
            ? withNested
              ? 'text-brand-700 pointer-events-none'
              : `${SELECTED} text-brand-700 pointer-events-none`
            : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900',
        )
      }
      startswith="true"
    >
      {({ isActive }) => (
        <>
          <div className="flex items-center gap-2.5 min-w-0">
            {Icon && (
              <Icon
                size={20}
                weight={isActive ? 'bold' : 'regular'}
                className={isActive ? 'text-brand-700' : 'text-neutral-500'}
              />
            )}
            <span
              className={cn(
                'truncate',
                isActive && !withNested
                  ? 'text-[13px] font-semibold'
                  : 'text-sm font-medium',
              )}
            >
              {label}
            </span>
          </div>
          {withNested &&
            (isActive ? <CaretUp size={16} /> : <CaretDown size={16} />)}
        </>
      )}
    </NavLink>
  );
}

NavItem.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.instanceOf(Object).isRequired,
  to: PropTypes.string.isRequired,
  withNested: PropTypes.bool.isRequired,
};

function OnboardingBadge() {
  const count = usePendingOnboardingCount();
  if (!count) return null;
  return (
    <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-warning-500 text-white text-[10px] font-bold tabular-nums flex items-center justify-center leading-none flex-shrink-0">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function NavSubItem({ navs }) {
  return (
    <div>
      {navs.map((nav) => (
        <NavLink
          key={nav?.path}
          to={nav?.path}
          className={({ isActive }) =>
            cn(
              'group relative h-9 flex items-center pl-11 pr-3 transition-colors',
              FOCUS,
              isActive
                ? `${SELECTED} text-brand-700 text-[13px] font-semibold`
                : 'text-sm font-medium text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900',
            )
          }
        >
          <span className="truncate flex-1">{startCase(nav?.title)}</span>
          {nav?.path === 'onboarding' && <OnboardingBadge />}
          {nav?.tooltip && (
            <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 hidden group-hover:flex items-center whitespace-nowrap rounded bg-neutral-800 px-2 py-1 text-[11px] text-white shadow-lg">
              {nav.tooltip}
            </span>
          )}
        </NavLink>
      ))}
    </div>
  );
}

NavSubItem.propTypes = {
  navs: PropTypes.instanceOf(Array).isRequired,
};

function Navigation({ className }) {
  const routes = useRoutePermission();
  return (
    <nav className={cn('w-full', className)}>
      <ul className="py-2">
        {routes.map((route) =>
          route.subRoutes ? (
            <li key={route.path}>
              <NavItem
                to={`/${route.path}`}
                label={startCase(route.title || route.path)}
                icon={route.icon}
                withNested
              />
              <Routes>
                <Route
                  path={`${route.path}/*`}
                  element={
                    <NavSubItem
                      navs={route.subRoutes.filter((x) =>
                        isUndefined(x?.index),
                      )}
                    />
                  }
                />
              </Routes>
            </li>
          ) : (
            <li key={route.path}>
              <NavItem
                to={`/${route.path}`}
                label={startCase(route.path)}
                icon={route.icon}
                withNested={false}
              />
            </li>
          ),
        )}
      </ul>
    </nav>
  );
}

Navigation.defaultProps = {
  className: '',
};

Navigation.propTypes = {
  className: PropTypes.string,
};

function SidebarFooter() {
  return (
    <div className="bg-neutral-50 border-t border-neutral-200 px-4 py-3 text-center">
      <div className="text-xs font-semibold text-neutral-700">
        eMessage Core
      </div>
      <div className="text-xs text-neutral-500 tabular-nums">
        © {new Date().getFullYear()} · v2.0.0
      </div>
    </div>
  );
}

function Sidebar({ isOpen, setIsOpen }) {
  const onClose = () => setIsOpen(false);
  return (
    <>
      {/* Below xl the rail becomes a slide-over over a neutral-900/55 scrim. */}
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 flex z-50"
          open
          onClose={onClose}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={onClose}
          >
            <Dialog.Overlay className="fixed inset-0 bg-neutral-900/55" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative w-[280px] flex flex-col bg-white border-r border-neutral-200">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
                    onClick={onClose}
                    type="button"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <X size={24} aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>
              <div className="h-header flex items-center px-4 border-b border-neutral-200">
                <Link to="/" className={cn('block', FOCUS)}>
                  <img className="h-8 w-auto" src={logo} alt="eMessage Core" />
                </Link>
              </div>
              <div className="h-full overflow-y-auto">
                <Navigation />
              </div>
              <SidebarFooter />
            </div>
          </Transition.Child>
          <div className="flex-shrink-0 w-14" aria-hidden="true" />
        </Dialog>
      </Transition.Root>

      {/* Static rail for desktop — 230px, white, hairline right border, no shadow. */}
      <div className="z-40 flex-shrink-0 hidden xl:flex flex-col h-full w-sidebar bg-white border-r border-neutral-200">
        <div className="h-header flex-shrink-0 flex items-center px-4 border-b border-neutral-200">
          <Link to="/" className={cn('block', FOCUS)}>
            <img className="h-8 w-auto" src={logo} alt="eMessage Core" />
          </Link>
        </div>
        <div className="flex-1 overflow-y-auto">
          <Navigation />
        </div>
        <SidebarFooter />
      </div>
    </>
  );
}

Sidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};

export default Sidebar;
